import { Option, Select } from '@d-edge/overnight-hotelier-react'
import { useState } from 'react'
import { useChannels, useConfig } from '@/api/queries'
import type { ChannelMetrics } from '@/api/types'
import { DataTable, type Column, type FilterPreset } from '@/components/DataTable/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Icons } from '@/components/ui/Icons'
import { Widget } from '@/components/ui/Widget'
import { PosPolicyDialog } from '@/features/settings/PosPolicyDialog'
import { percent } from '@/lib/format'
import { links } from '@/lib/links'
import { BrokenBookingEnginesDialog } from './BrokenBookingEnginesDialog'
import { ChannelDetailsDialog } from './ChannelDetailsDialog'
import { ScrapingRatiosDialog } from './ScrapingRatiosDialog'
import { statusOf, trendOf, type Status } from './siteStatus'
import { TrendIcon } from './TrendIcon'
import styles from './SitesWidget.module.css'

const HOURS = [12, 24, 36]

// Mirrors what the backend counts: workers still marked Processing and active within the last 20 minutes.
const TASKS_HELP = 'Tasks: scraping workers busy on this POS right now, meaning instances marked as processing with activity in the last 20 minutes. Filter with processes.'

const PRESETS: FilterPreset[] = [
  { label: 'UB.IO', expression: 'mainDataProvider=ubio', title: 'Sites scraped through UB.IO' },
  { label: 'In progress', expression: 'progress<100', title: 'Synchronisations still running' },
  { label: '↑ improved', expression: 'trend=up', title: 'Success ratio better than 36 hours ago' },
  { label: '↓ decreased', expression: 'trend=down', title: 'Success ratio worse than 36 hours ago' },
  { label: 'Low success', expression: 'status=warning', title: 'Success ratio under 80%' },
  { label: 'High closed', expression: 'status=closed', title: 'Closed ratio over 30%' },
  { label: 'Top 15', expression: 'priorityOrder>=1 priorityOrder<16' },
]

function providerLabel(value: string | null): string {
  if (!value) return '–'
  if (value.toLowerCase().includes('ubio')) return 'UB.IO'
  if (value.toLowerCase().includes('residential')) return 'Residential'
  return value
}

export function SitesWidget({ className }: { className?: string }) {
  const [hours, setHours] = useState(12)
  const { data, isLoading, error } = useChannels(hours)
  const { data: config } = useConfig()
  const [selected, setSelected] = useState<ChannelMetrics | null>(null)
  const [policy, setPolicy] = useState<ChannelMetrics | null>(null)
  const [showRatios, setShowRatios] = useState(false)
  const [showBrokenBes, setShowBrokenBes] = useState(false)

  const columns: Column<ChannelMetrics>[] = [
    {
      key: 'priority',
      header: '',
      title: 'priority',
      width: '28px',
      align: 'center',
      render: (r) => (r.priority === 'Prio' ? <Icons.Star size={14} className={styles.star} aria-label="Priority site" /> : null),
    },
    { key: 'priorityOrder', header: '#', align: 'right', hideBelow: 'sm', render: (r) => (r.priorityOrder || '') },
    { key: 'mainDataProvider', header: 'Data', hideBelow: 'lg', render: (r) => <span className={styles.provider}>{providerLabel(r.mainDataProvider)}</span> },
    { key: 'proxyFactoryProvider', header: 'Proxy', hideBelow: 'lg', render: (r) => <span className={styles.provider}>{providerLabel(r.proxyFactoryProvider)}</span> },
    {
      key: 'name',
      header: 'Site',
      render: (r) => (
        <button type="button" className={styles.site} onClick={() => setSelected(r)} title="Open hotel details">
          {r.name}
        </button>
      ),
    },
    { key: 'customers', header: 'Clients', align: 'right', hideBelow: 'md' },
    {
      key: 'processes',
      header: <span className={styles.hint}>Tasks</span>,
      title: TASKS_HELP,
      align: 'right',
      hideBelow: 'lg',
    },
    { key: 'progress', header: 'Progress', hideBelow: 'sm', render: (r) => <ProgressCell value={r.progress} /> },
    { key: 'status', header: 'Status', align: 'center', width: '96px', value: statusOf, render: (r) => <StatusBadge status={statusOf(r)} /> },
    {
      key: 'successRatio',
      header: 'Success',
      align: 'right',
      render: (r) => (
        <span className={styles.ratio}>
          {percent(r.successRatio)}
          <TrendIcon trend={trendOf(r)} reference={r.referenceSuccessRatio} />
        </span>
      ),
    },
    { key: 'trend', header: 'Trend', value: trendOf, hidden: true },
    { key: 'openRatio', header: 'Open', align: 'right', hideBelow: 'md', render: (r) => <span className={styles.ratio}>{percent(r.openRatio)}</span> },
    {
      key: 'closedRatio',
      header: 'Closed',
      align: 'right',
      render: (r) => <span className={`${styles.ratio} ${r.closedRatio > 30 ? 'u-danger' : ''}`}>{percent(r.closedRatio)}</span>,
    },
    {
      key: 'policyAction',
      header: '',
      sortable: false,
      width: '70px',
      render: (r) => (
        <span className="u-row">
          <IconButton label="Edit policy" onClick={() => setPolicy(r)}>
            <Icons.Edit size={14} />
          </IconButton>
          {links.grafanaPos(config?.links, r.name) && (
            <a href={links.grafanaPos(config?.links, r.name)} target="_blank" rel="noreferrer" title="Open in Grafana" className="u-row">
              <Icons.External size={14} />
            </a>
          )}
        </span>
      ),
    },
  ]

  return (
    <Widget
      title="RateScreener sites"
      loading={isLoading}
      className={className}
      actions={
        <>
          <Select value={String(hours)} onChange={(value) => setHours(Number(value))} aria-label="Time window" className={styles.select}>
            {HOURS.map((h) => (
              <Option key={h} value={String(h)} label={`last ${h} hours`} />
            ))}
          </Select>
          <Button size="sm" onClick={() => setShowRatios(true)}>
            Broken competitors
          </Button>
          <Button size="sm" onClick={() => setShowBrokenBes(true)}>
            Invalid booking engines
          </Button>
        </>
      }
    >
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(r) => `${r.channelId}:${r.name}`}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search: ubio successRatio>15"
        presets={PRESETS}
        dense
        maxHeight="480px"
        copyText={(rows) => JSON.stringify(rows.map((r) => r.name), null, 2)}
      />
      {selected && <ChannelDetailsDialog channel={selected} hours={hours} onClose={() => setSelected(null)} />}
      {policy && <PosPolicyDialog channelId={policy.channelId} name={policy.name} onClose={() => setPolicy(null)} />}
      {showRatios && <ScrapingRatiosDialog hours={hours} onClose={() => setShowRatios(false)} />}
      {showBrokenBes && <BrokenBookingEnginesDialog onClose={() => setShowBrokenBes(false)} />}
    </Widget>
  )
}

function ProgressCell({ value }: { value: number }) {
  if (value >= 100) return <span className={styles.ratio}>100%</span>
  return (
    <div className={styles.progress} title={`${value.toFixed(1)}% of synchronisations finished`}>
      <div className={styles.progressFill} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      <span className={styles.progressText}>{value.toFixed(0)}%</span>
    </div>
  )
}

/** Filled pill with an icon: readable at a glance in a dense table. */
function StatusBadge({ status }: { status: Status }) {
  if (status === 'ok') return <Badge tone="ok" icon="circle-check" title="Healthy">OK</Badge>
  if (status === 'warning') return <Badge tone="danger" icon="triangle-exclamation" title="Success ratio under 80%">Low</Badge>
  return <Badge tone="warn" icon="octagon-exclamation" title="Closed ratio over 30%">Closed</Badge>
}
